import React, { useEffect, useState } from 'react';
import { Feature, getLocalizedProperty, ruleUsesReward } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfConfirmationDialog } from '@qualifio/ui-commons';
import {
  QlfFilterDetails,
  QlfMasterDetail,
  QlfSimpleDetail,
} from '@qualifio/ui-commons/dist/components/qlf-master-detail';
import { RewardAttributes, RewardType, isSoldOut } from '@loyalty/shared-common/rewards-api-client';
import { Skeleton } from '@mui/material';
import { faArchive } from '@fortawesome/pro-solid-svg-icons/faArchive';
import { faCopy } from '@fortawesome/pro-solid-svg-icons/faCopy';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { facRestore } from '@qualifio/ui-commons/dist/fonts/custom-fonts';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { QlfMasterDetailContainer } from '../../libs/shared/layout/QlfMasterDetailContainer';
import { RewardListItemInfo } from '../../libs/rewards/components/sidebar/RewardListItemInfo';
import { RewardsDetails } from '../../libs/rewards/components/detail';
import { useConfirmDialog } from '../../libs/shared/use-confirm-dialog';
import { useFeatures } from '../../libs/features/FeaturesContext';
import { useRewards } from '../../libs/rewards/RewardsContext';
import { useRules } from '../../libs/rules/RulesContext';

const useStyles = makeStyles(() => ({
  deletedRules: {
    marginTop: '20px',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: 'fit-content',
    textAlign: 'left',
    paddingLeft: '1.2em',
  },
}));

export function RewardsIndexPage() {
  const { t, i18n } = useTranslation();
  const {
    rewards,
    fetchRewards,
    isLoading,
    selectedReward,
    setSelectedReward,
    createReward,
    duplicate,
    archiveReward,
    restoreReward,
    deleteUnsavedReward,
    deleteUnsavedRewards,
  } = useRewards();
  const [searchField, setSearchField] = useState('');
  const [filters, setFilters] = useState(['available', 'soldout']);

  const { burningRules } = useRules();
  const classes = useStyles();
  const { askConfirmation, props, setProps, ...dialogProps } = useConfirmDialog();
  const { features } = useFeatures();
  const {
    remove: rewardRemove,
    restore: rewardRestore,
    write: rewardWrite,
  } = features[Feature.REWARD];

  const relatedBurningRules = burningRules.filter(rule =>
    ruleUsesReward(rule.actions, props?.rewardId),
  );

  /*
  const handleFilter = (filter: 'available' | 'archived' | 'soldout') =>
    setFilters({ ...filters, [filter]: !filters[filter] });

   */

  const templateHeader = () => (
    <QlfFilterDetails
      placeholder={t('rewards.sidebar.search')}
      onAdd={() => createReward()}
      disableAdd={!rewardWrite}
      onAddTooltip={rewardWrite ? '' : t('shared.action.permission.required')}
      onChange={e => setSearchField(e.target.value)}
      withFilters={{
        filters: [
          {
            type: 'dropDown',
            name: t('master-detail.filters.availability'),
            options: [
              {
                value: 'available',
                label: t('master-detail.filters.available'),
              },
              { value: 'archived', label: t('master-detail.filters.archived') },
              { value: 'soldout', label: t('master-detail.filters.soldout') },
            ],
            selectedOptions: filters,
            // fix qlfSearchbar ? string[] not assignable to (string|number)[]
            onChange: (value: any[]) => {
              setFilters(value);
            },
          },
        ],
      }}
    />
  );

  const templateRow = (props: { data: RewardAttributes }) => {
    const { data: reward } = props;

    const options = reward.deletedAt
      ? [
          {
            node: <p>{t('master-detail.actions.restore')}</p>,
            startIcon: <FontAwesomeIcon fixedWidth={true} icon={facRestore} />,
            callback: () => restoreReward(reward.id),
            disabled: !rewardRestore,
            tooltip: rewardRestore ? '' : t('shared.action.permission.required'),
          },
        ]
      : (reward as any)['isNew']
      ? [
          {
            node: <p>{t('generic.actions.hard-delete')}</p>,
            startIcon: <FontAwesomeIcon fixedWidth={true} icon={faTrash} />,
            callback: () => deleteUnsavedReward(reward.id),
            disabled: !rewardRemove,
            tooltip: rewardRemove ? '' : t('shared.action.permission.required'),
          },
        ]
      : [
          {
            node: <p>{t('master-detail.actions.archive')}</p>,
            startIcon: <FontAwesomeIcon fixedWidth={true} icon={faArchive} />,
            callback: () => {
              setProps({ rewardId: reward.id });
              askConfirmation(() => {
                archiveReward(reward.id);
                setSelectedReward(undefined);
              });
            },
            disabled: !rewardRemove,
            tooltip: rewardRemove ? '' : t('shared.action.permission.required'),
          },
          {
            node: <p>{t('master-detail.actions.duplicate')}</p>,
            startIcon: <FontAwesomeIcon fixedWidth={true} icon={faCopy} />,
            callback: () => duplicate(reward),
            disabled: !rewardWrite,
            tooltip: rewardWrite ? '' : t('shared.action.permission.required'),
          },
        ];

    return (
      <QlfSimpleDetail
        state={reward.id === selectedReward?.id ? 'active' : 'default'}
        classes={
          {
            submenu: 'rewards-sidebar-item-actions',
          } as any
        }
        options={options}
        callback={() => setSelectedReward(props.data)}
        content={getLocalizedProperty(reward, 'name', i18n.language)}
        secondaryText={
          reward.type === RewardType.CAMPAIGN_CODE ? (
            <></>
          ) : (
            <RewardListItemInfo
              won={reward.promocodeStats?.wonCodes}
              total={reward.promocodeStats?.totalCodes}
            />
          )
        }
        image={getLocalizedProperty(reward, 'image', i18n.language)}
      />
    );
  };

  const newItems = rewards
    .filter(
      reward =>
        !searchField || getLocalizedProperty(reward, 'name', i18n.language)?.includes(searchField),
    )
    .filter(reward => {
      return (
        (filters.includes('available') && !reward.deletedAt && !isSoldOut(reward)) ||
        (filters.includes('archived') && reward.deletedAt) ||
        (filters.includes('soldout') && isSoldOut(reward) && !reward.deletedAt)
      );
    });

  if (!!selectedReward && !newItems.find(item => item.id === selectedReward.id)) {
    setSelectedReward(undefined);
  }

  useEffect(() => {
    if (!rewards) {
      fetchRewards();
    }
    // eslint-disable-next-line
    return () => {
      deleteUnsavedRewards();
    };
  }, []);

  return (
    <>
      <QlfMasterDetailContainer>
        {isLoading(fetchRewards) ? (
          <Skeleton />
        ) : (
          <QlfMasterDetail
            templateHeader={templateHeader}
            templateRow={templateRow}
            templateDetail={RewardsDetails}
            items={newItems}
          />
        )}
      </QlfMasterDetailContainer>
      <QlfConfirmationDialog
        {...dialogProps}
        title={t('rewards.confirm-archive.title')}
        content={
          (
            <>
              <div>
                {relatedBurningRules.length
                  ? t('rewards.confirm-archive.message')
                  : t('rewards.confirm-archive.message-no-rules')}
              </div>
              {relatedBurningRules.length > 0 && (
                <ul className={classes.deletedRules}>
                  {relatedBurningRules.map(rule => (
                    <li key={rule.id}>{rule.name}</li>
                  ))}
                </ul>
              )}
            </>
          ) as any
        }
        color="warning"
        confirmButtonText={t('master-detail.actions.archive')}
        cancelButtonText={t('generic.actions.cancel')}
      />
    </>
  );
}
